import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  ThumbUp,
} from '@mui/icons-material';

// Placeholder news data - replace with actual API call later
const placeholderNews = [
  {
    id: 'news-1',
    title: 'Global Climate Summit Reaches Historic Agreement',
    category: 'politics',
    content: 'World leaders have reached a groundbreaking agreement on climate change at the latest summit. The deal includes ambitious targets for reducing carbon emissions by 2030. This represents a significant step forward in global environmental cooperation.',
    author: 'Global News',
    thumbnail: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=400&h=300&fit=crop',
    publishedAt: '2024-01-15',
    views: 4500,
    likes: 234,
  },
  {
    id: 'news-2',
    title: 'Premier League Match Highlights: Manchester United vs Liverpool',
    category: 'sport',
    content: 'In an electrifying match, Manchester United secured a 2-1 victory against Liverpool. The game featured spectacular goals and intense moments throughout. Both teams displayed exceptional skill and determination.',
    author: 'Sports Daily',
    thumbnail: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&h=300&fit=crop',
    publishedAt: '2024-01-14',
    views: 12000,
    likes: 567,
  },
  {
    id: 'news-3',
    title: 'Tech Giant Announces Revolutionary AI Breakthrough',
    category: 'technology',
    content: 'A major technology company has unveiled a new artificial intelligence system that promises to revolutionize how we interact with computers. The breakthrough could change the landscape of digital communication.',
    author: 'Tech Weekly',
    thumbnail: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&h=300&fit=crop',
    publishedAt: '2024-01-13',
    views: 8900,
    likes: 445,
  },
  {
    id: 'news-4',
    title: 'New Study Reveals Benefits of Exercise',
    category: 'health',
    content: 'A comprehensive new study shows that regular exercise provides significant benefits for both physical and mental health. Researchers recommend at least 30 minutes of moderate activity daily.',
    author: 'Health Journal',
    thumbnail: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=300&fit=crop',
    publishedAt: '2024-01-10',
    views: 7200,
    likes: 378,
  },
];

const News = () => {
  const navigate = useNavigate();
  const [itemsToShow, setItemsToShow] = useState(12);

  const handlePostClick = (post) => {
    // Navigate to news detail page when implemented
    // navigate(`/news/${post.id}`);
    console.log('News clicked:', post);
  };

  const handleLoadMore = () => {
    setItemsToShow(prev => prev + 12);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/profile')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" fontWeight="600">
          News
        </Typography>
      </Box>

      {placeholderNews.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {placeholderNews.slice(0, itemsToShow).map((post) => (
              <Box
                key={post.id}
                onClick={() => handlePostClick(post)}
                sx={{
                  display: 'flex',
                  gap: 3,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  pb: 4,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="h5" fontWeight="600" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    {post.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      lineHeight: 1.6,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.content}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                    {post.category && (
                      <Chip
                        label={post.category}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    )}
                    {post.author && (
                      <Typography variant="caption" color="text.secondary">
                        {post.author}
                      </Typography>
                    )}
                    {post.publishedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(post.publishedAt).toLocaleDateString()}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                      {post.views !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Visibility sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {post.views}
                          </Typography>
                        </Box>
                      )}
                      {post.likes !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ThumbUp sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {post.likes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {post.thumbnail && (
                  <Box
                    component="img"
                    src={post.thumbnail}
                    alt={post.title}
                    sx={{
                      width: { xs: 120, md: 200 },
                      height: { xs: 120, md: 200 },
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
          {placeholderNews.length > itemsToShow && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                sx={{
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderRadius: '25px',
                }}
              >
                Load More
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No news articles available
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default News;
